import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, ChevronDown, X, CalendarIcon, Sparkles, Pencil, Calculator } from "lucide-react";
import { format } from "date-fns";
import type { ColumnDef } from "@/hooks/useTableColumns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  isCheckboxChecked,
  CHECKBOX_CHECKED_VALUE,
  parseDateOnly,
  localDateToDateOnly,
  formatCurrencyValue,
  formatNumberValue,
  trafficLightOptionsWithLabels,
  trafficLightOption,
} from "@/lib/utils/custom-column";
import EditableLocationField from "@/components/shared/EditableLocationField";
import { useCurrency } from "@/contexts/CurrencyContext";
import { toast } from "sonner";
import { displayDate } from "@/lib/i18n/display";

interface Props {
  col: ColumnDef;
  /**
   * Declared as the union it actually is. `customProperties`/`propertiesJson` is
   * an untyped JSON scalar, so a cell value reaches us as a number, boolean,
   * single-element array, or null just as often as a string — typing it `string`
   * only hid that from callers.
   */
  value: string | number | boolean | string[] | null | undefined;
  onChange: (value: string) => void;
  researching?: boolean;
}

// Validation helpers
function isValidEmail(v: string) {
  return !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
function isValidUrl(v: string) {
  return !v || /^(https?:\/\/)?[\w.-]+\.\w{2,}(\/.*)?$/i.test(v);
}
function isValidPhone(v: string) {
  return !v || /^[+\d\s().-]{0,20}$/.test(v);
}
function isValidNumber(v: string) {
  return !v || /^-?\d+(\.\d+)?$/.test(v.trim());
}
/**
 * Display text for a materialized formula value.
 *
 * The stored value is a bare string with no type alongside it in the cell, so this
 * sniffs the value rather than reading the column's result type — the same approach the
 * numeric branch already took. Dates are checked FIRST: `Number("2026-08-28")` is NaN so
 * the order is not load-bearing today, but a date is the more specific shape and reading
 * it second invites a future numeric tweak to swallow it.
 */
function formatFormulaValue(v: string): string {
  const raw = v.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    // parseDateOnly, not `new Date`, which reads a bare date as UTC midnight and renders
    // the previous day west of Greenwich.
    const parsed = parseDateOnly(raw);
    if (parsed) return displayDate(parsed);
  }
  return Number.isFinite(Number(raw)) && raw !== "" ? formatNumberValue(raw) : v;
}
export function formatPhone(v: string): string {
  const digits = v.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return v; // return as-is if non-standard length
}

export default function CustomColumnCell({ col, value: rawValue, onChange, researching }: Props) {
  const { t: tRoot } = useTranslation();
  const type = col.type;
  const options = col.selectOptions || [];
  const { symbol: currencySymbol } = useCurrency();

  // Normalize the untyped value ONCE, here, so every branch below can treat it as
  // a string. Each branch used to guard on its own (or forget to): the formula
  // branch called `value.trim()` directly and threw "t.trim is not a function" on
  // a numeric formula result, which took down the entire People/Companies/Deals
  // grid through the error boundary. New branches inherit the guard for free.
  // `String(null)` is "null", so null/undefined are mapped to "" explicitly.
  const value = rawValue == null ? "" : String(rawValue);

  // AI researching state — sparkles icon with animated ellipsis dots
  if (researching) {
    return (
      <div className="flex items-center gap-1.5 py-0.5">
        <Sparkles className="h-3.5 w-3.5 text-primary/50 shrink-0" />
        <span className="flex items-center gap-[3px]">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="inline-block h-[5px] w-[5px] rounded-full bg-primary/40"
              style={{
                animation: 'aiDotBounce 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </span>
      </div>
    );
  }

  // Formula — computed and materialized by the backend, so it is never
  // editable here. Falling through to the default input would render an edit
  // affordance over a value any save would immediately overwrite.
  if (type === "formula") {
    return (
      <div className="flex items-center gap-1.5 py-0.5 text-sm">
        <Calculator className="h-3 w-3 text-muted-foreground shrink-0" />
        {value ? (
          <span className="truncate">{formatFormulaValue(value)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
    );
  }

  // Single select
  if (type === "single-select") {
    return <SingleSelectCell value={value} options={options} onChange={onChange} />;
  }

  // Multi select
  if (type === "multi-select") {
    return <MultiSelectCell value={value} options={options} onChange={onChange} />;
  }

  // Checkbox
  if (type === "checkbox") {
    return (
      <Checkbox
        checked={isCheckboxChecked(value)}
        onCheckedChange={(c) => onChange(c ? CHECKBOX_CHECKED_VALUE : "")}
        className="h-4 w-4"
      />
    );
  }

  // Traffic light
  if (type === "traffic-light") {
    return <TrafficLightCell value={value} onChange={onChange} labels={options} />;
  }

  // Currency
  if (type === "currency") {
    return <ValidatedInput value={value} onChange={onChange} validate={isValidNumber} format={(v) => formatCurrencyValue(v, currencySymbol)} placeholder="" inputMode="decimal" saveRaw invalidMessage={tRoot("grid.validation.number")} />;
  }

  // Number
  if (type === "number") {
    return <ValidatedInput value={value} onChange={onChange} validate={isValidNumber} format={formatNumberValue} placeholder="" inputMode="decimal" saveRaw invalidMessage={tRoot("grid.validation.number")} />;
  }

  // Email
  if (type === "email") {
    return <ValidatedInput value={value} onChange={onChange} validate={isValidEmail} placeholder="" inputMode="email" invalidMessage={tRoot("grid.validation.email")} />;
  }

  // Phone
  if (type === "phone") {
    return <ValidatedInput value={value} onChange={onChange} validate={isValidPhone} format={formatPhone} placeholder="" inputMode="tel" invalidMessage={tRoot("grid.validation.phone")} />;
  }

  // URL
  if (type === "url") {
    return <ValidatedInput value={value} onChange={onChange} validate={isValidUrl} placeholder="" inputMode="url" invalidMessage={tRoot("grid.validation.url")} renderAsLink />;
  }

  // Date
  if (type === "date") {
    return <DatePickerCell value={value} onChange={onChange} />;
  }

  // Location — reuse the same rich Google Places typeahead as the default
  // "location" column (grid + detail sheets), so custom location fields get real
  // autocomplete instead of the old preview-only hardcoded list.
  if (type === "location") {
    return <EditableLocationField value={value} onChange={onChange} />;
  }

  // Text, AI — default editable
  return <ValidatedInput value={value} onChange={onChange} placeholder="" />;
}

// ─── Sub-components ──────────────────────────────────

function ValidatedInput({
  value,
  onChange,
  validate,
  format,
  placeholder,
  inputMode,
  saveRaw,
  invalidMessage,
  renderAsLink,
}: {
  value: string;
  onChange: (v: string) => void;
  validate?: (v: string) => boolean;
  format?: (v: string) => string;
  placeholder?: string;
  inputMode?: "text" | "decimal" | "email" | "tel" | "url";
  saveRaw?: boolean; // If true, save raw value without formatting (for numbers/currency)
  invalidMessage?: string;
  renderAsLink?: boolean;
}) {
  const { t } = useTranslation();
  // `value` is already normalized to a string by CustomColumnCell, so validators
  // like isValidNumber can call .trim() on it safely.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const isValid = validate ? validate(draft) : true;
  const hasValue = value.trim().length > 0;
  const displayValue = hasValue ? (format ? format(value) : value) : "";

  // Ensure focus when entering edit mode
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  if (!editing) {
    if (renderAsLink && hasValue) {
      const href = /^https?:\/\//i.test(value) ? value : `https://${value}`;
      return (
        <span className="inline-flex items-center gap-1 min-h-[24px] group max-w-full">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-sm text-accent hover:underline truncate min-w-0"
            title={value}
          >
            {displayValue}
          </a>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDraft(value); setEditing(true); }}
            aria-label={t("common.edit")}
            className="shrink-0"
          >
            <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
          </button>
        </span>
      );
    }
    return (
      <span
        onClick={(e) => { e.stopPropagation(); setDraft(value); setEditing(true); }}
        className="cursor-pointer text-sm text-foreground hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 -my-0.5 transition min-w-[40px] min-h-[24px] inline-flex items-center"
      >
        {displayValue ? (
          displayValue
        ) : placeholder ? (
          <span className="text-muted-foreground">{placeholder}</span>
        ) : (
          <span className="text-muted-foreground select-none">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
        )}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        if (isValid) {
          const final = saveRaw ? draft : (format ? format(draft) : draft);
          onChange(final);
          setEditing(false);
        } else {
          // Invalid on blur: revert the draft and exit edit mode so the field
          // doesn't silently discard the user's input without any explanation.
          if (invalidMessage) toast.error(invalidMessage);
          setDraft(value);
          setEditing(false);
        }
      }}
      onKeyDown={e => {
        e.stopPropagation();
        if (e.key === "Enter") {
          if (isValid) {
            // For numbers/currency, save raw value without formatting
            const final = saveRaw ? draft : (format ? format(draft) : draft);
            onChange(final);
            setEditing(false);
          } else {
            // Invalid on Enter: keep the input open + focused so the user can
            // correct it, and surface a toast explaining what's needed.
            if (invalidMessage) toast.error(invalidMessage);
            e.preventDefault();
            return;
          }
        }
        if (e.key === "Escape") setEditing(false);
      }}
      onClick={e => e.stopPropagation()}
      inputMode={inputMode}
      placeholder={placeholder}
      autoFocus
      className={cn(
        "text-sm bg-transparent outline-none border-b px-0.5 py-0.5 w-full",
        isValid ? "border-accent" : "border-destructive text-destructive"
      )}
    />
  );
}

function SingleSelectCell({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Already string-normalized upstream, so a single-element array (["a"]) from
  // customProperties compares correctly against the options here.
  const selected = value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="group flex items-center gap-1 text-sm hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 -my-0.5 transition min-w-[60px] max-w-full text-left overflow-hidden"
          title={selected || undefined}
        >
          <span className="flex-1 truncate">
            {selected || <span className="text-muted-foreground">{t("grid.cell.select")}</span>}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start">
        {options.map(opt => (
          <button
            key={opt}
            onClick={() => { onChange(opt); setOpen(false); }}
            className={cn(
              "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-muted transition text-left",
              selected === opt && "font-medium text-accent"
            )}
          >
            {selected === opt && <Check className="h-3 w-3 shrink-0" />}
            <span className={selected !== opt ? "pl-5" : ""}>{opt}</span>
          </button>
        ))}
        {selected && (
          <button
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-muted transition text-left text-muted-foreground border-t border-border mt-1 pt-1"
          >
            <X className="h-3 w-3" /> {t("grid.cell.clear")}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function MultiSelectCell({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => (value ? value.split("||").filter(Boolean) : []),
    [value],
  );

  const toggle = (opt: string) => {
    const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
    onChange(next.join("||"));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="group flex items-center gap-1 text-sm hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 -my-0.5 transition min-w-[60px] max-w-full text-left overflow-hidden"
          title={selected.join(", ")}
        >
          {selected.length > 0 ? (
            <span className="flex items-center gap-1 flex-nowrap overflow-hidden min-w-0">
              <Badge variant="secondary" className="text-xs py-0 px-1.5 font-normal shrink truncate max-w-[10rem]">
                {selected[0]}
              </Badge>
              {selected.length > 1 && (
                <span className="text-xs text-muted-foreground shrink-0">+{selected.length - 1}</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">{t("grid.cell.select")}</span>
          )}
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0 ml-auto opacity-0 group-hover:opacity-100 transition" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start">
        <div className="max-h-48 overflow-y-auto">
          {options.map(opt => (
            <div
              key={opt}
              onClick={() => toggle(opt)}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-muted transition text-left cursor-pointer"
            >
              <Checkbox checked={selected.includes(opt)} className="h-3.5 w-3.5" tabIndex={-1} />
              <span>{opt}</span>
            </div>
          ))}
        </div>
        {selected.length > 0 && (
          <button
            onClick={() => onChange("")}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-muted transition text-left text-muted-foreground border-t border-border mt-1 pt-1"
          >
            <X className="h-3 w-3" /> {t("grid.cell.clearAll")}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function TrafficLightCell({ value, onChange, labels }: { value: string; onChange: (v: string) => void; labels?: string[] }) {
  const [open, setOpen] = useState(false);
  // Coerce via trafficLightOption: customProperties can arrive as a single-element
  // array (["green"]), which a strict `.find(c => c.value === value)` never matched
  // — so the grid always showed "None". The helper String()-coerces like the other
  // cells (checkbox/date) already do.
  const opts = trafficLightOptionsWithLabels(labels);
  const current = trafficLightOption(value, labels);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 text-xs hover:bg-muted/50 rounded px-1.5 py-1 transition">
          <span className={cn("h-3 w-3 rounded-full shrink-0", current.color)} />
          <span className="text-muted-foreground">{current.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-32 p-1" align="start">
        {opts.map(c => (
          <button
            key={c.value}
            onClick={() => { onChange(c.value); setOpen(false); }}
            className={cn(
              "w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-muted transition",
              current.value === c.value && "font-medium"
            )}
          >
            <span className={cn("h-2.5 w-2.5 rounded-full", c.color)} />
            {c.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function DatePickerCell({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);

  // Parse the stored "YYYY-MM-DD" as a LOCAL date so display doesn't shift to the
  // previous day in negative-offset timezones (see parseDateOnly).
  const dateValue = useMemo(() => parseDateOnly(value), [value]);

  const displayValue = dateValue ? displayDate(dateValue) : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          onClick={e => e.stopPropagation()}
          className="cursor-pointer text-sm text-foreground hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 -my-0.5 transition min-w-[40px] min-h-[24px] inline-flex items-center gap-1"
        >
          <CalendarIcon className="h-3 w-3 text-muted-foreground shrink-0" />
          {displayValue || <span className="text-muted-foreground select-none">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" onClick={e => e.stopPropagation()}>
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={(d) => {
            if (d) {
              // Serialize from local Y/M/D so the persisted day matches the pick
              // in any timezone (toISOString would drop a day for +offset zones).
              onChange(localDateToDateOnly(d));
            } else {
              onChange("");
            }
            setOpen(false);
          }}
          initialFocus
          className="p-3 pointer-events-auto"
        />
      </PopoverContent>
    </Popover>
  );
}

